import { CRM_URL } from "./AccountMenu";
import type { NavGroup } from "./Sidebar";

/** Право CRM на «Карту спящих гостей» (backend/auth/permissions.catalog.ts). */
export const SLEEPING_GUESTS_PERMISSION = "courier_reports_sleeping_guests:view";

/**
 * Видит ли пользователь ссылку на карту спящих гостей в CRM. В SSO-токене SMS-центра лежат все
 * права роли CRM, поэтому смотрим именно на своё право карты (или «*» у администратора УК).
 * Зонтичное «Отчёты по курьерам» не считаем: в токене оно приходит и как подразумеваемое от
 * любого другого отчёта раздела, а такой пользователь карту не откроет.
 */
export function hasSleepingGuestsAccess(permissions: readonly string[] | null | undefined): boolean {
  return !!permissions && (permissions.includes("*") || permissions.includes(SLEEPING_GUESTS_PERMISSION));
}

/**
 * Единственный список разделов маркетинга; прямые ссылки сохраняют прежние адреса.
 * Порядок и иконки групп — как в меню прототипа «SMS-центр» (каркас CRM v2):
 * Кампании · Финансы · Контент · Клиенты и данные · Аналитика · Администрирование · Рабочее место.
 * Права пунктов прежние: «Финансы» — только с правом billing, «Счета партнёров» — администратору УК,
 * «Администрирование» — только администратору. «Журнал действий» открыт всем (Go режет его областью):
 * администратору — в «Администрировании», остальным — в «Рабочем месте».
 */
export function navigationGroups({
  finance,
  clients,
  admin,
  partner = false,
  crmReports = false,
  approvals = null,
}: {
  finance: boolean;
  clients: boolean;
  admin: boolean;
  /** Кабинет партнёра: первым пунктом «Главная» (итог недели, баланс, сценарии). */
  partner?: boolean;
  /**
   * Есть право на «Карту спящих гостей» в CRM (см. `hasSleepingGuestsAccess`): только тогда в
   * «Аналитике» показываем ссылку на неё. Без права ссылка вела бы на страницу с отказом.
   */
  crmReports?: boolean;
  /**
   * Очередь «На согласовании» (пакет 7b, п.23) — пункт только администратору УК с правом решать; число — сколько
   * кампаний ждут решения (бейдж). null — пункта нет (согласование не для этого пользователя).
   */
  approvals?: number | null;
}): NavGroup[] {
  return [
    {
      key: "campaigns",
      label: "Кампании",
      icon: "send",
      items: [
        ...(partner ? [{ to: "/home", label: "Главная", icon: "home" as const }] : []),
        { to: "/campaign-dashboard", label: "Обзор и заказы", icon: "bar" },
        {
          to: "/campaigns",
          label: "Кампании",
          icon: "grid",
          matchPrefixes: ["/campaigns"],
          excludePrefixes: ["/campaigns/results"],
          excludePattern: /^\/campaigns\/[^/]+\/results$/,
        },
        ...(approvals !== null
          ? [
              {
                to: "/approvals",
                label: "На согласовании",
                icon: "check" as const,
                badge: approvals,
              },
            ]
          : []),
        {
          to: "/campaigns/results",
          label: "Статистика кампании",
          icon: "bar",
          matchPattern: /^\/campaigns\/[^/]+\/results$/,
        },
        { to: "/recommendations", label: "Возвращаемость", icon: "refresh" },
      ],
    },
    ...(finance
      ? [
          {
            key: "finance" as const,
            label: "Финансы",
            icon: "wallet" as const,
            items: [
              {
                to: "/finance",
                label: "Обзор финансов",
                icon: "wallet" as const,
              },
              {
                to: "/billing",
                label: "Баланс и счета",
                icon: "card" as const,
              },
              { to: "/spending", label: "Расходы", icon: "bar" as const },
              ...(clients
                ? [
                    {
                      to: "/clients",
                      label: "Счета партнёров",
                      icon: "users" as const,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      key: "content",
      label: "Контент",
      icon: "layout",
      items: [
        {
          to: "/templates",
          label: "Шаблоны сообщений",
          icon: "doc",
          matchPrefixes: ["/templates"],
        },
        { to: "/promo-codes", label: "Промокоды", icon: "gift" },
      ],
    },
    {
      key: "data",
      label: "Клиенты и данные",
      icon: "users",
      items: [
        { to: "/guests", label: "Клиенты", icon: "users" },
        { to: "/data/actions", label: "Действия клиентов" },
        { to: "/data/orders", label: "Заказы" },
        { to: "/data/products", label: "Продукты" },
        { to: "/segments", label: "RFM-сегменты" },
      ],
    },
    {
      key: "analytics",
      label: "Аналитика",
      icon: "bar",
      items: [
        { to: "/reports/business", label: "Метрики бизнеса" },
        { to: "/reports/goals", label: "Цели бизнеса" },
        { to: "/reports/actions", label: "Целевые действия" },
        { to: "/reports/lifecycle", label: "Жизненный цикл" },
        { to: "/reports/cohorts", label: "Когорты удержания" },
        { to: "/reports/segments", label: "Отчёт по сегментам" },
        { to: "/reports/consents", label: "Согласия на рассылки" },
        { to: "/reports/uplift", label: "Кому помогает рассылка" },
        { to: "/reports/promotions", label: "Отчёт по акциям" },
        { to: "/reports/scenarios", label: "Отчёт по сценариям" },
        { to: "/reports/site", label: "Конверсия сайта" },
        { to: "/analytics", label: "Повторные покупки и LTV" },
        // Пакет 8b, п.46: карта живёт в CRM рядом с тепловой картой (там координаты адресов и права
        // по точкам). Сюда ничего не копируется — это только переход; сессия CRM общая (.brasol.ru).
        // Ссылка — только тем, у кого в токене есть право CRM на эту карту.
        ...(crmReports
          ? [{ to: `${CRM_URL}courier-reports/sleeping-guests`, label: "Карта спящих гостей", external: true }]
          : []),
      ],
    },
    ...(admin
      ? [
          {
            key: "settings" as const,
            label: "Администрирование",
            icon: "cog" as const,
            items: [
              {
                to: "/settings",
                label: "Настройки",
                icon: "cog" as const,
                matchPrefixes: ["/channels"],
                end: true,
              },
              {
                to: "/settings/integrations",
                label: "Внешние системы",
                icon: "cog" as const,
              },
              {
                to: "/settings/staff",
                label: "Персонал",
                icon: "users" as const,
              },
              // Журнал действий (пакет 9a): рядом с «Персоналом»; не-администраторам он стоит в «Рабочем месте».
              {
                to: "/settings/audit",
                label: "Журнал действий",
                icon: "doc" as const,
              },
              {
                to: "/settings/security",
                label: "Группы безопасности",
                icon: "users" as const,
              },
            ],
          },
        ]
      : []),
    {
      key: "workspace",
      label: "Рабочее место",
      icon: "briefcase",
      items: [
        { to: "/preferences", label: "Форматы и расчёты" },
        { to: "/problems", label: "Проблемы и развитие" },
        // Администратору журнал — в «Администрировании» (/settings/audit); остальным — здесь: Go отдаёт каждому
        // только его область (партнёр видит свои кампании и деньги).
        ...(admin
          ? []
          : [
              {
                to: "/audit",
                label: "Журнал действий",
                icon: "doc" as const,
              },
            ]),
        { to: "/howto", label: "Как это работает", icon: "book" },
      ],
    },
  ];
}
